import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
    LANGUAGES,
    SUPPORTED_LANG_CODES,
    HREFLANG_REGIONS,
    OG_LOCALE,
} from "../i18n";
import { COMPANY, COUNTRIES } from "../data/site";

const ORIGIN = "https://jagruthistones.com";
const OG_IMAGE = `${ORIGIN}/og-image.jpg`;

const upsert = (selector, tagName, attrs) => {
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement(tagName);
        document.head.appendChild(tag);
    }
    Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
    return tag;
};

// Strips any language prefix so we can rebuild the URL for every locale.
const basePathOf = (pathname) => {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length && SUPPORTED_LANG_CODES.includes(segments[0])) {
        segments.shift();
    }
    return segments.length ? `/${segments.join("/")}` : "/";
};

const urlFor = (lang, basePath) => {
    const suffix = basePath === "/" ? "" : basePath;
    return lang === "en" ? `${ORIGIN}${suffix || "/"}` : `${ORIGIN}/${lang}${suffix}`;
};

// Accepts an i18n key prefix ("meta.about") and keeps the whole head in sync
// with the active locale: title, description, keywords, canonical, Open Graph,
// Twitter, the country-qualified hreflang set, and localised JSON-LD.
export const usePageTitle = (metaKey) => {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const lang = i18n.language;

    const rawTitle = t(`${metaKey}.title`);
    const description = t(`${metaKey}.description`);
    const keywords = t("meta.keywords", { defaultValue: "" });

    useEffect(() => {
        const fullTitle = rawTitle
            ? `${rawTitle} | ${t("meta.base")}`
            : t("meta.base_home");
        document.title = fullTitle;

        const basePath = basePathOf(pathname);
        const canonical = urlFor(lang, basePath);

        upsert('meta[name="description"]', "meta", {
            name: "description",
            content: description,
        });
        if (keywords) {
            upsert('meta[name="keywords"]', "meta", {
                name: "keywords",
                content: keywords,
            });
        }
        upsert('link[rel="canonical"]', "link", {
            rel: "canonical",
            href: canonical,
        });

        // Open Graph
        const og = {
            "og:url": canonical,
            "og:title": fullTitle,
            "og:description": description,
            "og:locale": OG_LOCALE[lang] || "en_US",
            "og:image": OG_IMAGE,
            "og:type": "website",
            "og:site_name": COMPANY.legalName,
        };
        Object.entries(og).forEach(([property, content]) =>
            upsert(`meta[property="${property}"]`, "meta", { property, content }),
        );

        // Advertise the other locales to social crawlers.
        document.head
            .querySelectorAll('meta[property="og:locale:alternate"]')
            .forEach((el) => el.remove());
        LANGUAGES.filter((l) => l.code !== lang).forEach((l) => {
            const m = document.createElement("meta");
            m.setAttribute("property", "og:locale:alternate");
            m.setAttribute("content", OG_LOCALE[l.code]);
            document.head.appendChild(m);
        });

        // Twitter
        Object.entries({
            "twitter:card": "summary_large_image",
            "twitter:title": fullTitle,
            "twitter:description": description,
            "twitter:image": OG_IMAGE,
        }).forEach(([name, content]) =>
            upsert(`meta[name="${name}"]`, "meta", { name, content }),
        );

        // Country-qualified hreflang across every language, plus x-default.
        document.head
            .querySelectorAll('link[rel="alternate"][data-i18n-alt]')
            .forEach((el) => el.remove());

        const alternates = [];
        LANGUAGES.forEach((l) => {
            (HREFLANG_REGIONS[l.code] || [l.code]).forEach((tag) =>
                alternates.push([tag, urlFor(l.code, basePath)]),
            );
        });
        alternates.push(["x-default", urlFor("en", basePath)]);

        alternates.forEach(([hreflang, href]) => {
            const link = document.createElement("link");
            link.setAttribute("rel", "alternate");
            link.setAttribute("hreflang", hreflang);
            link.setAttribute("href", href);
            link.setAttribute("data-i18n-alt", "true");
            document.head.appendChild(link);
        });

        // Localised structured data, replacing the static build-time block.
        document.head
            .querySelectorAll("script[data-i18n-ld], script[data-static-ld]")
            .forEach((el) => el.remove());
        const ld = document.createElement("script");
        ld.setAttribute("type", "application/ld+json");
        ld.setAttribute("data-i18n-ld", "true");
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${ORIGIN}/#organization`,
            name: COMPANY.legalName,
            alternateName: COMPANY.short,
            description,
            url: canonical,
            inLanguage: lang,
            logo: `${ORIGIN}/favicon-512.png`,
            image: OG_IMAGE,
            email: COMPANY.email,
            telephone: COMPANY.phone.replace(/\s/g, ""),
            foundingDate: String(COMPANY.foundedYear),
            address: {
                "@type": "PostalAddress",
                streetAddress: `${COMPANY.address.line1}, ${COMPANY.address.line2}`,
                addressLocality: COMPANY.address.district,
                addressRegion: COMPANY.address.state,
                postalCode: COMPANY.address.postal,
                addressCountry: "IN",
            },
            areaServed: COUNTRIES.map((c) => ({
                "@type": "Country",
                name: c.name,
                identifier: c.code.toUpperCase(),
            })),
            knowsLanguage: LANGUAGES.map((l) => l.code),
            naics: "212311",
            knowsAbout: [
                "Granite rough blocks",
                "Cut granite slabs",
                "Granite export",
                "HSN 2516",
                "Monumental stone",
            ],
            sameAs: [`https://wa.me/${COMPANY.whatsapp}`],
        });
        document.head.appendChild(ld);

        document.documentElement.lang = lang;
    }, [rawTitle, description, keywords, lang, pathname, t]);
};
